import { useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SettingsPanel } from './tabs/SettingsPanel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@strivelabs/ui-core/v2';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarProvider,
  type LinkRenderer,
  type NavSection,
  useSidebar,
} from '@strivelabs/ui-blocks/v2';
import { useAppStore } from '@/store/appStore';
import { useIsStrivelabsUser } from '@/hooks/useIsStrivelabsUser';
import { useUserRole } from '@/hooks/useUserRole';
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics';
import { SidebarBrand } from './header/SidebarBrand';
import { CollapseToggle } from './header/CollapseToggle';
import { CreditWarning } from './footer/CreditWarning';
import { AccountMenu } from './footer/AccountMenu';
import { ChatTabIcon, WorkspaceTabIcon } from './icons/SidebarIcons';
import { WorkspaceTab } from './tabs/WorkspaceTab';
import { ChatTab } from './tabs/chat/ChatTab';
import { useNewChatShortcut } from './hooks/useNewChat';
import type { SidebarPanel } from './types';

/**
 * Everything between header and footer.
 *
 * The collapsed branch is the only place that knows about the rail: at 68px
 * there is no room for a tab strip or a chat list, so both are dropped rather
 * than squeezed.
 *
 * Guests take the same branch for a different reason: chat is not among the
 * routes they may visit, so a Chat tab would offer them a panel whose every
 * row redirects. Their nav is a single Reports entry, which needs no tab strip.
 */
function SidebarTabs({ sections }: { sections: NavSection[] }) {
  const { collapsed, resizing } = useSidebar();
  const { isGuest, isLoaded } = useUserRole();
  const panel = useAppStore(state => state.sidebarPanel);
  const setSidebarPanel = useAppStore(state => state.setSidebarPanel);
  const { pathname } = useLocation();

  // Under /ui/settings the middle region becomes the settings nav, in place of
  // the Workspace/Chat tabs. Derived from the URL, so entering or leaving
  // settings needs no extra state and the previous panel is restored on return.
  if (pathname.startsWith('/ui/settings')) {
    return (
      <SidebarContent>
        <SettingsPanel />
      </SidebarContent>
    );
  }

  // Until the role resolves, `isGuest` is false, so rendering now would flash a
  // guest the full tab strip and nav before it collapses to their single entry.
  // Skeleton until we know — the account row does the same.
  if (!isLoaded) {
    return (
      <SidebarContent>
        <div aria-hidden className="flex flex-col gap-1.5 px-1 py-1">
          {[0, 1, 2, 3, 4].map(row => (
            <div key={row} className="h-[26px] animate-pulse rounded-v2-lg bg-v2-sidebar-accent motion-reduce:animate-none" />
          ))}
        </div>
      </SidebarContent>
    );
  }

  if (collapsed || isGuest) {
    return (
      <SidebarContent>
        <WorkspaceTab sections={sections} />
      </SidebarContent>
    );
  }

  return (
    <Tabs
      value={panel}
      onValueChange={value => setSidebarPanel(value as SidebarPanel)}
      variant="segment"
      // Freeze the indicator mid-drag: it re-projects each frame as the panel
      // width changes, and springing every one of those reads as lag.
      transition={resizing ? { duration: 0 } : undefined}
      className="flex min-h-0 flex-1 flex-col px-2.5"
    >
      {/* The Workspace / Chat switcher (Home Shell design): a recessed
          alpha-black track with 30px segments, 12.5px medium labels and the
          rail's own tab glyphs. */}
      <TabsList className="w-full gap-0.5 rounded-v2-lg border-0 bg-black/[0.045] p-[3px]">
        <TabsTrigger
          value="workspace"
          className="h-[30px] gap-[5px] rounded-v2-md px-1 text-[12.5px] font-medium leading-[18px] tracking-[-0.2px] focus-visible:ring-offset-v2-sidebar"
        >
          <span className="grid place-items-center text-[15px]"><WorkspaceTabIcon /></span>
          Workspace
        </TabsTrigger>
        <TabsTrigger
          value="chat"
          className="h-[30px] gap-[5px] rounded-v2-md px-1 text-[12.5px] font-medium leading-[18px] tracking-[-0.2px] focus-visible:ring-offset-v2-sidebar"
        >
          <span className="grid place-items-center text-[15px]"><ChatTabIcon /></span>
          Chat
        </TabsTrigger>
      </TabsList>

      <SidebarContent className="mt-[14px] px-0 py-0">
        <TabsContent value="workspace" className="mt-0">
          <WorkspaceTab sections={sections} />
        </TabsContent>
        <TabsContent value="chat" className="mt-0 h-full">
          <ChatTab />
        </TabsContent>
      </SidebarContent>
    </Tabs>
  );
}

/**
 * Logo and collapse control on one 40px row.
 *
 * Expanded, both are visible side by side. In the rail there is only room for
 * one, so the mark holds the slot and gives way to the toggle on hover — the
 * mark identifies the product at rest, and the control appears when the pointer
 * says you are reaching for it. Both stay mounted and swap opacity rather than
 * conditionally rendering, so the toggle is still reachable by keyboard;
 * `focus-within` reveals it for that case.
 */
function SidebarMasthead() {
  const { collapsed } = useSidebar();

  if (!collapsed) {
    return (
      <div className="flex h-10 items-center justify-between gap-0.5">
        <SidebarBrand />
        <CollapseToggle />
      </div>
    );
  }

  return (
    <div className="relative grid h-10 place-items-center">
      <span className="col-start-1 row-start-1 transition-opacity duration-v2-quick group-hover/sidebar:opacity-0 group-focus-within/sidebar:opacity-0 motion-reduce:transition-none">
        <SidebarBrand />
      </span>
      <span className="pointer-events-none col-start-1 row-start-1 opacity-0 transition-opacity duration-v2-quick group-hover/sidebar:pointer-events-auto group-hover/sidebar:opacity-100 group-focus-within/sidebar:pointer-events-auto group-focus-within/sidebar:opacity-100 motion-reduce:transition-none">
        <CollapseToggle />
      </span>
    </div>
  );
}

/**
 * Reports sidebar collapse/expand. Watches the provider's state rather than
 * wrapping {@link CollapseToggle}'s click, because ⌘B is bound inside the
 * ui-core Sidebar shell — tracking only the button would miss every keyboard
 * use. Renders nothing; it just needs to be inside the provider to read it.
 */
function CollapseTracker() {
  const { collapsed } = useSidebar();
  const trackEvent = useTrack();
  const previousRef = useRef<boolean | null>(null);

  useEffect(() => {
    // Skip the first run: the initial (possibly persisted) state is not a
    // change the user just made.
    if (previousRef.current === null) {
      previousRef.current = collapsed;
      return;
    }
    if (previousRef.current === collapsed) return;
    previousRef.current = collapsed;
    trackEvent(ANALYTICS_EVENTS.SIDEBAR_TOGGLED, { collapsed });
  }, [collapsed, trackEvent]);

  return null;
}

/** Header-to-switcher gap: 18px expanded, 8px in the rail (Home Shell design). */
function HeaderSpacer() {
  const { collapsed } = useSidebar();
  return <div aria-hidden className={collapsed ? 'h-2' : 'h-[18px]'} />;
}

/** The application sidebar. Assembles the shell; all logic lives below it. */
export function AppSidebar({ sections }: { sections: NavSection[] }) {
  const { pathname } = useLocation();
  const { isGuest, isLoaded } = useUserRole();
  const { isStrivelabsUser } = useIsStrivelabsUser();
  useNewChatShortcut({ enabled: isLoaded && !isGuest });

  const renderLink: LinkRenderer = useCallback(
    ({ href, className, children, onClick, ...rest }) => (
      <Link to={href} className={className} onClick={onClick} {...rest}>
        {children}
      </Link>
    ),
    [],
  );

  return (
    <SidebarProvider sections={sections} currentPath={pathname} renderLink={renderLink}>
      <CollapseTracker />
      <Sidebar label="Main">
        <SidebarHeader className="gap-0 pb-0">
          <SidebarMasthead />
          <HeaderSpacer />
        </SidebarHeader>

        <SidebarTabs sections={sections} />

        <SidebarFooter>
          {/* Staff-only: it links into Billing, which nobody else can open. */}
          {isStrivelabsUser && <CreditWarning />}
          <AccountMenu />
        </SidebarFooter>
      </Sidebar>
    </SidebarProvider>
  );
}
